export function isIosDevice(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports as Mac; disambiguate via touch support
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

// True Safari only — excludes Chrome/Firefox/Edge on any platform, which all
// include "Safari" in their UA string too.
function isSafariBrowser(): boolean {
  return /^((?!chrome|crios|fxios|edg|android).)*safari/i.test(navigator.userAgent);
}

// Apple requires a site be installed (Home Screen on iOS, Dock on macOS)
// before Safari allows real Web Push — see isIosDevice() for the iOS side.
// Without this check, calls into pushManager on macOS Safari without the
// PWA added to the Dock don't reject, they hang indefinitely.
export function isSafariOnMacNeedingInstall(): boolean {
  const isMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints <= 1;
  return isMac && isSafariBrowser();
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function guessDeviceLabel(): string {
  const ua = navigator.userAgent;

  let browser: string | null = null;
  if (/edg\//i.test(ua)) browser = 'Edge';
  else if (/crios/i.test(ua)) browser = 'Chrome';
  else if (/fxios|firefox/i.test(ua)) browser = 'Firefox';
  else if (/chrome/i.test(ua)) browser = 'Chrome';
  else if (/safari/i.test(ua)) browser = 'Safari';

  let os: string | null = null;
  if (/ipad/i.test(ua)) os = 'iPad';
  else if (/iphone|ipod/i.test(ua)) os = 'iPhone';
  else if (/android/i.test(ua)) os = 'Android';
  else if (/mac os x/i.test(ua)) os = navigator.maxTouchPoints > 1 ? 'iPad' : 'Mac';
  else if (/windows/i.test(ua)) os = 'Windows';
  else if (/linux/i.test(ua)) os = 'Linux';

  if (browser && os) return `${browser} on ${os}`;
  if (browser) return browser;
  if (os) return os;
  // Neither recognized — fall back to whatever real signal the UA gives us
  // (e.g. "Mozilla/5.0") rather than an uninformative placeholder.
  return ua.split(' ')[0] || 'Unknown browser';
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}
